import { useEffect, useState } from "react";
import ToggleSwitch from "../../components/ToggleSwitch";
import DataTable, { Column } from "../../components/DataTable";
import { Feature } from "../../types/schoolBranchType";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { getBranchFeatures } from "../../features/school-branch-controller/schoolBranchSlice";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { toast } from "sonner";
import { fetchStaffAccess } from "../../features/access-controller/AccessController";
import { useBranchCode } from "../../util/hooks/useBranch";
import { IS_LIVE } from "../../util/config/env";

const categoryOrder = ["COMMUNICATION", "FINANCE_AND_PAYMENTS", "OTHERS"];

export default function AcademicPermissions() {
  const dispatch = useDispatch<AppDispatch>();
  const { features, isLoading } = useSelector(
    (state: RootState) => state.schoolBranch,
  );

  const branchId = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchId,
  );

  useEffect(() => {
    dispatch(getBranchFeatures(branchId));
  }, [dispatch, branchId]);

  const sortedFeatures = [...features].sort(
    (a, b) =>
      categoryOrder.indexOf(a.featureCategory) -
      categoryOrder.indexOf(b.featureCategory),
  );

  const featuresWithCategories: (
    | Feature
    | { isCategory: true; category: string }
  )[] = [];

  sortedFeatures.forEach((feature, index) => {
    const prevItem = sortedFeatures[index - 1];
    const showCategory =
      !prevItem || prevItem.featureCategory !== feature.featureCategory;

    if (showCategory) {
      featuresWithCategories.push({
        isCategory: true,
        category: feature.featureCategory.replaceAll("_", " "),
      });
    }

    featuresWithCategories.push(feature);
  });

  const staffId = useSelector((state: RootState) => state.user?.user?.userId);

  const branchCode = useBranchCode();

  const handleToggle = async (feature: Feature) => {
    try {
      await SchoolBranchApis.toggleFeature(branchId, feature.featuresId!);

      dispatch(
        fetchStaffAccess({
          userId: Number(staffId),
          branchCode,
        }),
      );

      toast.success("Feature toggled successfully");
    } catch (error) {
      toast.error("Feature toggle failed");
    }
  };

  const columns: Column<Feature | { isCategory: true; category: string }>[] = [
    {
      label: "SL. NO.",
      accessor: "id",
      render: (item, index) => {
        if ("isCategory" in item) return null;
        return <span className="flex">{index}</span>;
      },
    },

    {
      label: "Feature List",
      accessor: "featureList",
      searchable: true,
      render: (item) => {
        if ("isCategory" in item) {
          return (
            <div className="text-neutral-300 font-medium text-xs">
              {item.category}
            </div>
          );
        }

        return (
          <div className="font-bold text-sm text-neutral-400">
            {item.featuresName}
          </div>
        );
      },
    },
    {
      label: "Enable/Disable",
      accessor: "canEdit",
      searchable: false,
      render: (item) => {
        if ("isCategory" in item) return null;

        // List of features to disable
        const disabledFeatures = [
          "Messaging",
          "Notice Board",
          "Calendar & Events",
          "Payroll Management",
          "Leave Management",
        ];

        const isDisabled = disabledFeatures.includes(item.featuresName) && IS_LIVE;

        return (
          <ToggleSwitch
            defaultChecked={item.active}
            disabled={isDisabled} 
            onToggle={(v) => handleToggle(item)}
          />
        );
      },
    },
  ];

  return (
    <div className="px-4 md:px-9 py-7 flex flex-col gap-8 overflow-x-auto max-w-2xl">
      <span className="text-xs font-medium text-error-500">
        *Only the features enabled here will appear for your staffs.
      </span>
      <DataTable
        columns={columns}
        data={featuresWithCategories}
        isLoading={isLoading}
      />
    </div>
  );
}
